/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import React from "react";

import { FormElement } from "../FormElement";
import { IFormElementConfig } from "../IFormElementConfig";
import { FormModel } from "../FormModel";
import { IFormData } from "../IFormData";
import { FORM_EL_TYPE } from "../FormElementTypes";
import { IFormElementInlineConfig } from "./IFormElementInlineConfig";

export type TFormHTMLContentValue = string|((data: IFormData, model: FormModel, currentElement: FormHTMLContent) => React.ReactElement);

/**
 * Element config
 */
export interface IFormHTMLContentConfig extends IFormElementConfig, IFormElementInlineConfig {
	/** HTML contents */
	content?: TFormHTMLContentValue;

	/** If to display without padding */
	noPadding?: boolean;
}

/**
 * HTML Content
 */
export class FormHTMLContent extends FormElement<IFormHTMLContentConfig> {

	/**
	 * Element constructor
	 *
	 * @param model Model instance
	 * @param parent Parent element
	 * @param config Element configuration
	 */
	public constructor(
		protected model: FormModel,
		protected parent: FormElement<any>,
		protected config: IFormHTMLContentConfig
	) {

		super(model, parent, FORM_EL_TYPE.HTML_CONTENT, config);

	}

	/**
	 * Updates the element
	 *
	 * @param resetState If to reset state
	 */
	public update(resetState: boolean = false) {

		if (typeof this.config.content === "function") {
			this.emit("update");
		}

	}

	/**
	 * Returns evaluated HTML content
	 */
	public evalContent(): string|React.ReactElement {

		if (typeof this.config.content === "function") {

			return this.config.content(
				this.model.getData(),
				this.model,
				this
			);

		} else {

			return this.config.content;

		}

	}

}

/**
 * Declares HTML content
 *
 * @param config Config
 * @param content Content
 */
export function HTMLContent(
	config: IFormHTMLContentConfig,
	content: TFormHTMLContentValue
) {

	const _config = {
		content: content,
		...config
	};

	return (model, parent) => new FormHTMLContent(model, parent, _config);

}
